import {useCallback, useEffect, useState} from "react"
import axios from "axios"
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom"
import {
    Alert,
    Badge,
    Button,
    Card,
    Spinner,
    Tab,
    Tabs,
} from "react-bootstrap"

import {useAuth} from "../../context/AuthProvider"
import RoomPowerPlanningOverviewComponent, {
    PlanningOverview,
    PlanningOverviewRoom,
} from "./RoomPowerPlanningOverviewComponent"
import {
    Configuration,
    Equipment,
    FreeOutletEditor,
    FreeOutletType,
    Installation,
    Layout,
    NewSeat,
    PlanDocument,
    PlanningData,
    Seat,
    SeatRemovalImpact,
    SeatPlan,
    Survey,
    SurveyTask,
} from "./room-power-planning/types"
import {
    layoutDisplayName,
} from "./room-power-planning/display"
import RoomPlanDocumentsComponent from
    "./room-power-planning/RoomPlanDocumentsComponent"
import {
    RoomEquipmentComponent,
    RoomInstallationComponent,
    RoomPowerGraphComponent,
    RoomSeatLayoutsComponent,
    RoomSeatPlanDataComponent,
} from "./room-power-planning/RoomPlanningEditorTabs"
import RoomSurveyComponent from
    "./room-power-planning/RoomSurveyComponent"
import RoomSeatPlanComponent from
    "./room-power-planning/RoomSeatPlanComponent"
import {
    RoomOutletEditorModal,
    RoomSeatCreationModal,
    RoomSeatEditorModal,
} from "./room-power-planning/RoomPlanningEditorModals"
import MeasurementSketch from
    "./room-power-planning/MeasurementSketch"
import PageHeader from "../Navigation/PageHeader"
import RoomPlanUploadModal from
    "./room-power-planning/RoomPlanUploadModal"

export default function RoomPowerPlanningComponent() {
    const auth = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams<{roomId?: string}>()
    const [searchParams, setSearchParams] = useSearchParams()
    const isAdmin = auth.hasGate("admin")
    const roomId = params.roomId ?? ""
    const editingMode = Boolean(
        roomId && location.pathname.endsWith("/edit")
    )
    const roomTab = searchParams.get("tab") ?? "plan"
    const [data, setData] = useState<PlanningData | null>(null)
    const [layoutKey, setLayoutKey] = useState("")
    const [configurationKey, setConfigurationKey] = useState("")
    const [equipmentKey, setEquipmentKey] = useState("")
    const [editingSeat, setEditingSeat] = useState<Seat | null>(null)
    const [seatSaving, setSeatSaving] = useState(false)
    const [seatError, setSeatError] = useState<string | null>(null)
    const [editingOutletPresent, setEditingOutletPresent] = useState(false)
    const [editingSocketCount, setEditingSocketCount] = useState(1)
    const [editingFuseLabel, setEditingFuseLabel] = useState("")
    const [outletTool, setOutletTool] = useState<FreeOutletType | null>(null)
    const [noWritingSurfaceTool, setNoWritingSurfaceTool] = useState(false)
    const [seatStructureTool, setSeatStructureTool] =
        useState<"ADD" | "DELETE" | null>(null)
    const [newSeat, setNewSeat] = useState<NewSeat | null>(null)
    const [newSeatSaving, setNewSeatSaving] = useState(false)
    const [newSeatError, setNewSeatError] = useState<string | null>(null)
    const [freeOutletEditor, setFreeOutletEditor] =
        useState<FreeOutletEditor | null>(null)
    const [outletSaving, setOutletSaving] = useState(false)
    const [outletError, setOutletError] = useState<string | null>(null)
    const [surveySavingKey, setSurveySavingKey] = useState<string | null>(null)
    const [overview, setOverview] = useState<PlanningOverview | null>(null)
    const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(null)
    const [uploadRoom, setUploadRoom] =
        useState<PlanningOverviewRoom | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const loadOverview = useCallback(async () => {
        if (!isAdmin) return
        try {
            const response = await axios.get<PlanningOverview>(
                "/api/room-power-planning/overview",
                auth.axiosAuthHeader()
            )
            setOverview(response.data)
        } catch {
            setError("Die Gesamtübersicht konnte nicht geladen werden.")
        }
    }, [auth, isAdmin])

    useEffect(() => { void loadOverview() }, [loadOverview])

    useEffect(() => {
        setData(null)
        setLayoutKey("")
        setConfigurationKey("")
        setEquipmentKey("")
        setEditingSeat(null)
        setFreeOutletEditor(null)
        setOutletTool(null)
        setNoWritingSurfaceTool(false)
        setSeatStructureTool(null)
        setNewSeat(null)
        setError(null)
    }, [roomId])

    const load = useCallback(async () => {
        if (!roomId) return
        setLoading(true)
        try {
            const response = await axios.get<PlanningData>(
                `/api/room-power-planning/rooms/${roomId}`,
                auth.axiosAuthHeader()
            )
            setData({
                ...response.data,
                layouts: response.data.layouts.map(layout => ({
                    ...layout,
                    name: layoutDisplayName(layout),
                })),
            })
            setLayoutKey(previous => previous ||
                response.data.layouts[0]?.key || "")
            setConfigurationKey(previous => previous ||
                response.data.configurations[0]?.key || "")
            setEquipmentKey(previous => previous ||
                response.data.equipmentTypes[0]?.key || "")
            setError(null)
        } catch {
            setError("Stromplanungsdaten konnten nicht geladen werden.")
        } finally {
            setLoading(false)
        }
    }, [auth, roomId])

    useEffect(() => { void load() }, [load])

    const selectedLayout = data?.layouts.find(item => item.key === layoutKey)
    const selectOverviewRoom = useCallback((overviewRoom: PlanningOverviewRoom) => {
        navigate(`/admin/room-power-planning/${overviewRoom.room._id}`)
    }, [navigate])
    const setOperationalStatus = useCallback(async (
        item: PlanningOverviewRoom,
        operationalStatus: "active" | "inactive"
    ) => {
        try {
            await axios.put(
                `/api/room-power-planning/rooms/${item.room._id}/` +
                    "operational-status",
                {operationalStatus},
                auth.axiosAuthHeader()
            )
            await loadOverview()
        } catch {
            setError("Der Betriebsstatus konnte nicht gespeichert werden.")
        }
    }, [auth, loadOverview])
    const purgePlanningData = useCallback(async (
        item: PlanningOverviewRoom
    ) => {
        const roomLabel = `${item.room.building?.identifier ?? ""} ` +
            `${item.room.identifier}`.trim()
        if (!window.confirm(
            `Alle Sitzplatz-, Sitzplan-, Vermessungs- und ` +
            `Bestromungsdaten für ${roomLabel} unwiderruflich löschen? ` +
            `Der Raum selbst bleibt erhalten.`
        )) return
        if (window.prompt(
            "Zur Bestätigung DELETE eingeben:"
        ) !== "DELETE") return
        try {
            await axios.delete(
                `/api/room-power-planning/rooms/${item.room._id}/planning-data`,
                {
                    ...auth.axiosAuthHeader(),
                    data: {confirmation: "DELETE"},
                }
            )
            await loadOverview()
        } catch {
            setError("Die Planungsdaten konnten nicht gelöscht werden.")
        }
    }, [auth, loadOverview])
    if (!isAdmin) return <div className="component">
        <Alert variant="danger">Diese Funktion ist nur für Administratoren verfügbar.</Alert>
    </div>

    const saveInstallation = async (value: Installation) => {
        await axios.put(
            `/api/room-power-planning/rooms/${roomId}/installation`,
            value,
            auth.axiosAuthHeader()
        )
        await load()
    }
    const saveSeatPlan = async (value: SeatPlan) => {
        await axios.put(
            `/api/room-power-planning/rooms/${roomId}/seat-plans/` +
            `${value.revision}`,
            value,
            auth.axiosAuthHeader()
        )
        await load()
    }
    const saveLayout = async (value: Layout) => {
        await axios.put(
            `/api/room-power-planning/rooms/${roomId}/layouts/${value.key}`,
            value,
            auth.axiosAuthHeader()
        )
        await load()
    }
    const generateLayout = async (
        name: string,
        minimumFreeSeats: number
    ) => {
        try {
            const response = await axios.post<Layout>(
                `/api/room-power-planning/rooms/${roomId}/layouts/generate`,
                {name, minimumFreeSeats},
                auth.axiosAuthHeader()
            )
            setLayoutKey(response.data.key)
            await load()
        } catch {
            setError("Die Sitzplatzaufteilung konnte nicht erzeugt werden.")
            throw new Error("Layout generation failed")
        }
    }
    const deleteLayout = async (layout: Layout) => {
        if (!window.confirm(
            `Das generierte Layout „${layout.name}“ löschen?`
        )) return
        try {
            await axios.delete(
                `/api/room-power-planning/rooms/${roomId}/layouts/${layout.key}`,
                auth.axiosAuthHeader()
            )
            setLayoutKey("")
            await load()
        } catch {
            setError("Das generierte Layout konnte nicht gelöscht werden.")
        }
    }
    const saveEquipment = async (value: Equipment) => {
        await axios.put(
            `/api/room-power-planning/equipment-types/${value.key}`,
            value,
            auth.axiosAuthHeader()
        )
        await load()
    }
    const saveConfiguration = async (value: Configuration) => {
        await axios.put(
            `/api/room-power-planning/rooms/${roomId}/configurations/` +
            `${value.key}/${value.revision}`,
            value,
            auth.axiosAuthHeader()
        )
        await load()
    }
    const saveSeat = async () => {
        if (!editingSeat || !data?.seatPlan) return
        try {
            setSeatSaving(true)
            setSeatError(null)
            await axios.put(
                `/api/room-power-planning/rooms/${roomId}/seat-plans/` +
                `${data.seatPlan.revision}/seats/${editingSeat.key}`,
                {
                    hasWritingSurface: editingSeat.hasWritingSurface,
                    accessible: editingSeat.accessible,
                    position: editingSeat.position,
                    outlet: {
                        present: editingOutletPresent,
                        socketCount: editingSocketCount,
                        fuseLabel: editingFuseLabel,
                    },
                },
                auth.axiosAuthHeader()
            )
            setEditingSeat(null)
            await load()
        } catch (error: any) {
            setSeatError(error?.response?.status === 409
                ? "Dieser Sitz wird in einer aktiven Sitzplatzaufteilung verwendet. " +
                    "Entferne dort zuerst die Zuordnung."
                : "Der Sitzplatz konnte nicht gespeichert werden.")
        } finally {
            setSeatSaving(false)
        }
    }
    const toggleWritingSurface = async (seat: Seat) => {
        if (!data?.seatPlan) return
        try {
            setSeatError(null)
            const response = await axios.put<Seat>(
                `/api/room-power-planning/rooms/${roomId}/seat-plans/` +
                `${data.seatPlan.revision}/seats/${seat.key}`,
                {
                    hasWritingSurface: !seat.hasWritingSurface,
                    accessible: seat.accessible,
                    position: seat.position,
                },
                auth.axiosAuthHeader()
            )
            setData(previous => previous?.seatPlan ? {
                ...previous,
                seatPlan: {
                    ...previous.seatPlan,
                    seats: previous.seatPlan.seats.map(item =>
                        item.key === response.data.key ? response.data : item
                    ),
                },
            } : previous)
        } catch (requestError: any) {
            setError(requestError?.response?.status === 409
                ? "Der Sitz wird in einer aktiven Sitzplatzaufteilung verwendet."
                : "Die Schreibfläche konnte nicht geändert werden.")
        }
    }
    const proposeSeat = (position: {x: number; y: number}) => {
        if (!data?.seatPlan?.seats.length) return
        const sectionCenters = data.seatPlan.sections.map(section => {
            const sectionSeats = data.seatPlan!.seats.filter(
                seat => seat.section === section.key
            )
            return {
                key: section.key,
                distance: Math.abs(position.x - sectionSeats.reduce(
                    (sum, seat) => sum + seat.position.x, 0
                ) / sectionSeats.length),
            }
        }).sort((left, right) => left.distance - right.distance)
        const section = sectionCenters[0].key
        const sectionSeats = data.seatPlan.seats.filter(
            seat => seat.section === section
        )
        const nearestRowSeat = [...sectionSeats].sort((left, right) =>
            Math.abs(left.position.y - position.y) -
            Math.abs(right.position.y - position.y)
        )[0]
        const row = nearestRowSeat.row
        const occupiedColumns = new Set(sectionSeats.filter(
            seat => seat.row === row
        ).map(seat => seat.column))
        const maximumColumn = Math.max(...sectionSeats.map(seat => seat.column))
        const columnPositions = new Map<number, number>()
        for (let column = 1; column <= maximumColumn + 1; column++) {
            const matching = sectionSeats.filter(seat => seat.column === column)
            if (matching.length) {
                columnPositions.set(column, matching.reduce(
                    (sum, seat) => sum + seat.position.x, 0
                ) / matching.length)
            }
        }
        const knownColumnPositions = Array.from(columnPositions.entries())
            .sort(([left], [right]) => left - right)
        const pitches = knownColumnPositions.slice(1).map(
            ([column, x], index) =>
                (x - knownColumnPositions[index][1]) /
                (column - knownColumnPositions[index][0])
        )
        const pitch = pitches.length
            ? pitches.sort((left, right) => left - right)[
                Math.floor(pitches.length / 2)
            ]
            : 0.03
        const [anchorColumn, anchorX] = knownColumnPositions[0]
        const expectedX = (column: number) =>
            columnPositions.get(column) ??
            anchorX + (column - anchorColumn) * pitch
        const availableColumns = Array.from(
            {length: maximumColumn + 1},
            (_, index) => index + 1
        ).filter(column => !occupiedColumns.has(column))
        const column = availableColumns.sort((left, right) =>
            Math.abs(expectedX(left) - position.x) -
            Math.abs(expectedX(right) - position.x)
        )[0] ?? maximumColumn + 1
        setNewSeatError(null)
        setNewSeat({
            section,
            row,
            column,
            position,
            hasWritingSurface: true,
            accessible: false,
        })
    }
    const createSeat = async () => {
        if (!newSeat || !data?.seatPlan) return
        try {
            setNewSeatSaving(true)
            setNewSeatError(null)
            await axios.post(
                `/api/room-power-planning/rooms/${roomId}/seat-plans/` +
                `${data.seatPlan.revision}/seats`,
                newSeat,
                auth.axiosAuthHeader()
            )
            setNewSeat(null)
            await load()
        } catch (requestError: any) {
            setNewSeatError(requestError?.response?.status === 409
                ? "Diese Rasterposition ist bereits belegt."
                : "Der Sitzplatz konnte nicht hinzugefügt werden.")
        } finally {
            setNewSeatSaving(false)
        }
    }
    const deleteSeat = async (seat: Seat) => {
        if (!data?.seatPlan) return
        try {
            const url = `/api/room-power-planning/rooms/${roomId}/seat-plans/` +
                `${data.seatPlan.revision}/seats/${seat.key}`
            const response = await axios.get<SeatRemovalImpact>(
                `${url}/removal-impact`,
                auth.axiosAuthHeader()
            )
            const impact = response.data
            const dependencies = [
                ...impact.layouts.map(item => `Layout: ${item.name}`),
                ...impact.outlets.map(item =>
                    `Steckdose: ${item.label || item.key}`),
                ...impact.configurations.map(item =>
                    `Stromkonfiguration: ${item.name}`),
            ]
            if (dependencies.length) {
                window.alert(
                    `Sitz ${seat.key} kann nicht entfernt werden:\n\n` +
                    dependencies.join("\n")
                )
                return
            }
            if (!window.confirm(
                `Sitz ${seat.key} aus Block ${seat.section}, Reihe ` +
                `${seat.row}, Platz ${seat.column} wirklich entfernen?`
            )) return
            await axios.delete(url, {
                ...auth.axiosAuthHeader(),
                data: {confirmation: seat.key},
            })
            await load()
        } catch {
            setError("Der Sitzplatz konnte nicht entfernt werden.")
        }
    }
    const initializeRoom = async () => {
        if (!roomId || !editingMode) return
        try {
            setLoading(true)
            await axios.post(
                `/api/room-power-planning/rooms/${roomId}/initialize`,
                {},
                auth.axiosAuthHeader()
            )
            await Promise.all([load(), loadOverview()])
        } catch {
            setError("Der Hörsaal konnte nicht eingepflegt werden.")
            setLoading(false)
        }
    }
    const saveFreeOutlet = async () => {
        if (!freeOutletEditor) return
        try {
            setOutletSaving(true)
            setOutletError(null)
            const url = `/api/room-power-planning/rooms/${roomId}/` +
                `installation/outlets` +
                (freeOutletEditor.key ? `/${freeOutletEditor.key}` : "")
            await axios.request({
                method: freeOutletEditor.key ? "put" : "post",
                url,
                data: freeOutletEditor,
                ...auth.axiosAuthHeader(),
            })
            setFreeOutletEditor(null)
            setOutletTool(null)
            await load()
        } catch {
            setOutletError("Die Steckdose konnte nicht gespeichert werden.")
        } finally {
            setOutletSaving(false)
        }
    }
    const deleteFreeOutlet = async () => {
        if (!freeOutletEditor?.key) return
        try {
            setOutletSaving(true)
            await axios.delete(
                `/api/room-power-planning/rooms/${roomId}/installation/` +
                `outlets/${freeOutletEditor.key}`,
                auth.axiosAuthHeader()
            )
            setFreeOutletEditor(null)
            await load()
        } catch {
            setOutletError("Die Steckdose konnte nicht entfernt werden.")
        } finally {
            setOutletSaving(false)
        }
    }
    const generateSurvey = async () => {
        try {
            setSurveySavingKey("GENERATE")
            await axios.post(
                `/api/room-power-planning/rooms/${roomId}/survey/generate`,
                {},
                auth.axiosAuthHeader()
            )
            await load()
        } catch {
            setError("Die Vermessungsaufgaben konnten nicht erzeugt werden.")
        } finally {
            setSurveySavingKey(null)
        }
    }
    const updateSurveyTask = (
        taskKey: string,
        updater: (task: SurveyTask) => SurveyTask
    ) => setData(previous => {
        if (!previous?.survey) return previous
        return {
            ...previous,
            survey: {
                ...previous.survey,
                tasks: previous.survey.tasks.map(task =>
                    task.key === taskKey ? updater(task) : task
                ),
            },
        }
    })
    const saveSurveyTask = async (task: SurveyTask) => {
        try {
            setSurveySavingKey(task.key)
            const response = await axios.put<Survey>(
                `/api/room-power-planning/rooms/${roomId}/survey/tasks/` +
                `${task.key}`,
                {inputs: task.inputs, note: task.note, status: task.status},
                auth.axiosAuthHeader()
            )
            setData(previous => previous
                ? {...previous, survey: response.data} : previous)
        } catch {
            setError(`Messaufgabe „${task.title}“ konnte nicht gespeichert werden.`)
        } finally {
            setSurveySavingKey(null)
        }
    }
    const saveSurveySettings = async (value: Survey) => {
        await axios.put(
            `/api/room-power-planning/rooms/${roomId}/survey`,
            value,
            auth.axiosAuthHeader()
        )
        await load()
    }
    const openPdf = async (document: PlanDocument) => {
        const pdfWindow = window.open("", "_blank")
        try {
            setOpeningDocumentId(document._id)
            const response = await axios.get<Blob>(
                `/api/room-power-planning/documents/${document._id}/pdf`,
                {...auth.axiosAuthHeader(), responseType: "blob"}
            )
            const url = URL.createObjectURL(response.data)
            if (pdfWindow) {
                pdfWindow.location.href = url
            } else {
                window.open(url, "_blank", "noopener,noreferrer")
            }
            window.setTimeout(() => URL.revokeObjectURL(url), 60000)
        } catch {
            pdfWindow?.close()
            setError(`PDF „${document.filename}“ konnte nicht geöffnet werden.`)
        } finally {
            setOpeningDocumentId(null)
        }
    }
    return <div className="component">
        {!roomId ? <>
            <PageHeader
                title="Hörsaalübersicht"
                icon="bi-bezier2"
                description="Sitzplan-PDFs und Bearbeitungsstand aller Hörsäle."
                breadcrumbs={[
                    {label: "Administration"},
                    {label: "Hörsaalplanung"},
                ]}
            />
            {error && <Alert variant="danger">{error}</Alert>}
            {!overview
                ? <div className="text-center py-5"><Spinner/></div>
                : <RoomPowerPlanningOverviewComponent
                    overview={overview}
                    onOpenRoom={selectOverviewRoom}
                    onOpenPdf={document => void openPdf(document)}
                    onUploadPdf={setUploadRoom}
                    onSetOperationalStatus={(room, status) =>
                        void setOperationalStatus(room, status)}
                    onPurgePlanningData={room =>
                        void purgePlanningData(room)}
                    openingDocumentId={openingDocumentId}
                />}
        </> : <>
            <PageHeader
                title={data
                    ? `${data.room.building?.identifier ?? ""} ` +
                        `${data.room.identifier} – ${data.room.name}`
                    : "Hörsaalplanung"}
                icon="bi-bezier2"
                description="Sitzplan, Vermessung und Stromversorgung des Hörsaals."
                breadcrumbs={[
                    {label: "Administration"},
                    {
                        label: "Hörsaalplanung",
                        to: "/admin/room-power-planning",
                    },
                    {label: data?.room.identifier ?? "Hörsaal"},
                ]}
                backTo="/admin/room-power-planning"
                backLabel="Zur Übersicht"
                actions={<Button
                    variant={editingMode ? "warning" : "outline-secondary"}
                    onClick={() => {
                        setEditingSeat(null)
                        navigate(
                            `/admin/room-power-planning/${roomId}` +
                            (editingMode ? "" : "/edit") +
                            location.search
                        )
                    }}>
                    <i className={`bi ${editingMode
                        ? "bi-unlock-fill" : "bi-pencil"} me-2`}/>
                    {editingMode ? "Bearbeitung beenden" : "Bearbeiten"}
                </Button>}
            />
        {editingMode && <Alert variant="warning" className="py-2">
            Bearbeitungsmodus für diesen Hörsaal aktiv.
        </Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? <div className="text-center py-5"><Spinner/></div> : data &&
            <Card className="mb-3"><Card.Body>
            <Tabs activeKey={roomTab}
                onSelect={key => {
                    if (!key) return
                    const next = new URLSearchParams(searchParams)
                    next.set("tab", key)
                    setSearchParams(next)
                }}>
                <Tab eventKey="documents" title={
                    <>PDFs &amp; To-dos{" "}
                        {data.todos.length > 0 &&
                            <Badge bg="warning" text="dark">
                                {data.todos.reduce((sum, todo) =>
                                    sum + todo.count, 0)}
                            </Badge>}
                    </>
                }>
                    <RoomPlanDocumentsComponent
                        room={data.room}
                        documents={data.documents}
                        todos={data.todos}
                        openingDocumentId={openingDocumentId}
                        onOpenPdf={document => void openPdf(document)}
                    />
                </Tab>
                <Tab eventKey="plan" title="Sitzplan">
                    <RoomSeatPlanComponent
                        data={data}
                        selectedLayout={selectedLayout}
                        layoutKey={layoutKey}
                        editing={editingMode}
                        outletTool={outletTool}
                        noWritingSurfaceTool={noWritingSurfaceTool}
                        seatStructureTool={seatStructureTool}
                        onInitialize={() => void initializeRoom()}
                        onSelectLayout={setLayoutKey}
                        onOutletToolChange={setOutletTool}
                        onNoWritingSurfaceToolChange={setNoWritingSurfaceTool}
                        onSeatStructureToolChange={setSeatStructureTool}
                        onCreateOutlet={(type, position) => {
                            setOutletError(null)
                            setFreeOutletEditor({type, position, label: "",
                                socketCount: 1, fuseLabel: "", note: ""})
                        }}
                        onEditOutlet={(outlet, circuit) => {
                            setOutletError(null)
                            setFreeOutletEditor({key: outlet.key,
                                type: outlet.type as FreeOutletType,
                                position: {...outlet.position!},
                                label: outlet.label ?? "",
                                socketCount: outlet.socketCount,
                                fuseLabel: circuit?.label ?? "",
                                note: outlet.note ?? ""})
                        }}
                        onEditSeat={(seat, outlet, circuit) => {
                            setSeatError(null)
                            setEditingOutletPresent(Boolean(outlet))
                            setEditingSocketCount(outlet?.socketCount ?? 1)
                            setEditingFuseLabel(circuit?.label ?? "")
                            setEditingSeat({...seat, position: {...seat.position}})
                        }}
                        onToggleWritingSurface={seat =>
                            void toggleWritingSurface(seat)}
                        onCreateSeat={proposeSeat}
                        onDeleteSeat={seat => void deleteSeat(seat)}
                    />
                </Tab>
                <Tab eventKey="survey" title="Vermessung">
                    <RoomSurveyComponent
                        survey={data.survey}
                        seatPlan={data.seatPlan}
                        editing={editingMode}
                        savingKey={surveySavingKey}
                        onGenerate={() => void generateSurvey()}
                        onUpdateTask={updateSurveyTask}
                        onSaveTask={task => void saveSurveyTask(task)}
                        onSaveSettings={saveSurveySettings}
                        renderSketch={task => data.seatPlan
                            ? <MeasurementSketch task={task}
                                seatPlan={data.seatPlan}/>
                            : null}
                    />
                </Tab>
                <Tab eventKey="installation" title="Elektroinstallation">
                    <RoomInstallationComponent
                        installation={data.installation}
                        editing={editingMode}
                        onSave={saveInstallation}
                    />
                </Tab>
                <Tab eventKey="seat-data" title="Sitzplandaten">
                    <RoomSeatPlanDataComponent
                        seatPlan={data.seatPlan}
                        editing={editingMode}
                        onSave={saveSeatPlan}
                    />
                </Tab>
                <Tab eventKey="layouts" title="Sitzplatzaufteilungen">
                    <RoomSeatLayoutsComponent
                        layouts={data.layouts}
                        selectedKey={layoutKey}
                        editing={editingMode}
                        onSelect={setLayoutKey}
                        onSave={saveLayout}
                        onGenerate={generateLayout}
                        onDelete={deleteLayout}
                    />
                </Tab>
                <Tab eventKey="equipment" title="Material">
                    <RoomEquipmentComponent
                        equipment={data.equipmentTypes}
                        selectedKey={equipmentKey}
                        editing={editingMode}
                        onSelect={setEquipmentKey}
                        onSave={saveEquipment}
                    />
                </Tab>
                <Tab eventKey="graph" title="Graph">
                    <RoomPowerGraphComponent
                        configurations={data.configurations}
                        selectedKey={configurationKey}
                        editing={editingMode}
                        onSelect={setConfigurationKey}
                        onSave={saveConfiguration}
                    />
                </Tab>
            </Tabs>
            </Card.Body></Card>}
        <RoomOutletEditorModal
            value={freeOutletEditor}
            saving={outletSaving}
            error={outletError}
            onChange={setFreeOutletEditor}
            onClose={() => setFreeOutletEditor(null)}
            onSave={() => void saveFreeOutlet()}
            onDelete={() => void deleteFreeOutlet()}
        />
        <RoomSeatEditorModal
            seat={editingSeat}
            saving={seatSaving}
            error={seatError}
            outletPresent={editingOutletPresent}
            socketCount={editingSocketCount}
            fuseLabel={editingFuseLabel}
            onSeatChange={setEditingSeat}
            onOutletPresentChange={setEditingOutletPresent}
            onSocketCountChange={setEditingSocketCount}
            onFuseLabelChange={setEditingFuseLabel}
            onClose={() => setEditingSeat(null)}
            onSave={() => void saveSeat()}
        />
        <RoomSeatCreationModal
            value={newSeat}
            seatPlan={data?.seatPlan ?? null}
            saving={newSeatSaving}
            error={newSeatError}
            onChange={setNewSeat}
            onClose={() => setNewSeat(null)}
            onSave={() => void createSeat()}
        />
        </>}
        <RoomPlanUploadModal
            show={Boolean(uploadRoom)}
            room={uploadRoom?.room ?? null}
            onClose={() => setUploadRoom(null)}
            onUploaded={() => {
                void loadOverview()
                if (roomId) void load()
            }}
        />
    </div>
}
